//@ts-nocheck
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useComboBox } from '@react-aria/combobox';
import { FocusScope } from '@react-aria/focus';
import { PressResponder, useFocus, useHover } from '@react-aria/interactions';
import { useOverlayPosition } from '@react-aria/overlays';
import { HiddenSelect, useSelect } from '@react-aria/select';
import { mergeProps } from '@react-aria/utils';
import { useUnwrapDOMRef } from '@react-spectrum/utils';
import { useComboBoxState } from '@react-stately/combobox';
import { AsyncListData } from '@react-stately/data';
import { ListLayoutOptions } from '@react-stately/layout';
import { ComboBoxProps } from '@react-types/combobox';
import { DOMRefValue, FocusableRefValue } from '@react-types/shared';
import cn from 'classnames';
import MediaQuery from 'react-responsive';

import {
  ComboBoxButton,
  FocusRing,
  getComboBoxWrapperWithPopOverStyles,
  Label,
  ListBoxBase,
  OverlayOptions,
  Popover,
  positionConstants,
  useListBoxLayout
} from '@nw/omega-design-system';

import '@nw/omega-design-system/PopOver/index.css';
import '@nw/omega-design-system/Tray/index.css';

import { getDataMaskAttribute, trimStringToLength } from '@nw/shared/utils';

import Oval3LoaderIcon from '../../icons/Oval3LoaderIcon';
import { getComboBoxAllowedCharacters } from '../../utils/ComboBoxUtils';

import { TextFieldHint } from '../TextFieldHint';

import { MobileComboBox } from './MobileCombobox';
import { LoaderWrapper } from './styledComponents';

export interface ComboBoxPropsTypes {
  error: string | undefined;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  listItemStyles?: string;
  itemContentStyles?: string;
  helperText?: string;
  isDisabled?: boolean;
  apiStatus?: string;
  label?: string;
  autoFocus?: boolean;
  overlayOptions?: OverlayOptions;
  getPopoverPositionStyles?: (
    positionProps: React.HTMLAttributes<Element>,
    triggerRef: React.RefObject<HTMLElement>
  ) => Record<string, any>;
  customHeader?: (props: { onClose: () => void }) => JSX.Element;
  trayContainerClassName?: string;
  trayChildrenWrapperClassName?: string;
  listboxLayoutOptions?: ListLayoutOptions<any>;
  bottomSheetHeaderStyles?: string;
  onLoadMore?: () => any;
  onSelectionChange;
  getInputTextFromValue: (value: string, list) => string;
  getValueFromInputText: (value: string, list) => string;
  initialValue: string;
  list: AsyncListData<any>;
  getDisplayValueFromSelectedState?: (T: any) => string;
  renderLoader?: () => React.ReactElement | undefined;
  hintClassName?: string;
  enableDataMask?: boolean;
}

const getPopoverPositionStylesFn = (
  positionProps: React.HTMLAttributes<Element>,
  triggerRef: React.RefObject<HTMLElement>,
  inputRef: React.RefObject<HTMLElement>
) => {
  return {
    ...positionProps.style,
    maxHeight:
      typeof positionProps.style?.maxHeight === 'string'
        ? parseInt(positionProps.style.maxHeight) - 5
        : positionProps.style?.maxHeight
          ? positionProps.style.maxHeight - 5
          : undefined,
    paddingTop: '2px',
    paddingBottom: '2px',
    width:
      inputRef?.current && triggerRef?.current
        ? triggerRef?.current?.offsetWidth + inputRef.current?.offsetWidth + 40
        : 0,
    top:
      typeof positionProps.style?.top === 'string'
        ? parseInt(positionProps.style.top) + 12
        : positionProps.style?.top
          ? positionProps.style.top + 12
          : undefined
  };
};

export function ComboBoxComponent(
  props: ComboBoxProps<any> & ComboBoxPropsTypes
) {
  const inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement> =
    React.useRef(null);

  const {
    error,
    isDisabled,
    autoFocus,
    helperText,
    fieldContainerStyles,
    wrapperWithPopOverStyles,
    inputFieldStyles,
    buttonStyles,
    popOverStyles,
    overlayOptions = {},
    getPopoverPositionStyles = getPopoverPositionStylesFn,
    label,
    onInputChange,
    listboxLayoutOptions = {},
    selectedKey,
    onSelectionChange,
    getInputTextFromValue,
    getValueFromInputText,
    initialValue,
    list,
    getDisplayValueFromSelectedState,
    hintClassName,
    enableDataMask
  } = props;

  const popoverRef = useRef<DOMRefValue<HTMLDivElement>>(null);
  const unwrappedPopoverRef = useUnwrapDOMRef(popoverRef);
  const triggerRef = useRef<FocusableRefValue<HTMLElement>>(null);
  const unwrappedTriggerRef = useUnwrapDOMRef(triggerRef);
  const listBoxRef = useRef(null);

  const isAsync = list.loadingState != null;

  const updateValues = (value: string) => {
    try {
      let updatedValue = getComboBoxAllowedCharacters(value);
      updatedValue = trimStringToLength(updatedValue, 150);
      return updatedValue;
    } catch {
      return value;
    }
  };

  const onSelectionChangeFunc = (value: string) => {
    list.setSelectedKeys(new Set([value]));
    list.setFilterText(getInputTextFromValue(value, list.items));
    onSelectionChange(value);
  };

  const onChange = (value: string) => {
    const updatedValue = updateValues(value);
    list.setFilterText(updatedValue);
    onInputChange && onInputChange(updatedValue);
    const stateId = getValueFromInputText(value, list.items);
    if (stateId) {
      onSelectionChangeFunc(stateId);
    }
  };

  const state = useComboBoxState({
    ...props,
    allowsEmptyCollection: isAsync,
    items: list.items,
    autoFocus: false,
    selectedKey: selectedKey,
    onSelectionChange: onSelectionChangeFunc,
    onInputChange: onChange,
    inputValue: list.filterText
  });

  useEffect(() => {
    const updateSelectedKey = async () => {
      if (!state.selectedKey && initialValue) {
        const selectedKey = await getValueFromInputText(
          initialValue,
          list.items
        );

        if (selectedKey && selectedKey !== state.selectedKey) {
          state.setSelectedKey(selectedKey);
        }
      }
    };

    updateSelectedKey();
  }, [list.items]);

  useEffect(() => {
    window.visualViewport.addEventListener('resize', resizeHandler);
  }, []);

  const resizeHandler = () => {
    setTimeout(() => {
      const element = document.querySelector(`[data-testid="tray"]`);
      element?.scrollIntoView({ block: 'end' });
    }, 1000);
  };

  const {
    buttonProps: triggerProps,
    inputProps,
    labelProps
  } = useComboBox(
    {
      ...props,
      inputRef: inputRef as RefObject<HTMLInputElement>,
      buttonRef: unwrappedTriggerRef,
      listBoxRef,
      popoverRef: unwrappedPopoverRef,
      autoFocus: false
    },
    state
  );

  const layout = useListBoxLayout(state, listboxLayoutOptions);

  const [isFocus, setFocus] = useState(false);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = inputProps;
    setFocus(true);
    if (onFocus) onFocus(e);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = inputProps;
    setFocus(false);
    if (onBlur) onBlur(e);
  };

  const { hoverProps } = useHover({ isDisabled });

  const [isFocusWithin, setFocusWithin] = React.useState(false);
  const { focusProps } = useFocus({
    onFocusChange: (isFocusWithin) => setFocusWithin(isFocusWithin)
  });

  const ref = React.useRef(null);
  const { menuProps } = useSelect(props as any, state, ref);

  const { overlayProps: positionProps, placement } = useOverlayPosition({
    targetRef: unwrappedTriggerRef,
    overlayRef: unwrappedPopoverRef,
    scrollRef: listBoxRef,
    offset: 0,
    isOpen: state.isOpen,
    placement: positionConstants['bottomRight'],
    onClose: state.close,
    ...overlayOptions
  });

  const renderNoResults = () => (
    <div
      className={cn(
        'm-auto pb-3 w-full h-full flex items-center justify-center flex-col'
      )}
    >
      {/* {!(isAsync && list.loadingState === 'loading') && (
        <span>no items found</span>
        TODO: update results not found svg
      )} */}
      <p className={cn('text-center text-blue-gray-600 font-medium')}>
        {isAsync && list.loadingState === 'loading' ? (
          'Loading...'
        ) : (
          <span>No results found</span>
        )}
      </p>
    </div>
  );

  const listBox = (
    <FocusScope restoreFocus>
      <ListBoxBase
        {...menuProps}
        state={state}
        ref={listBoxRef}
        layout={layout}
        shouldSelectOnPressUp
        focusOnPointerEnter
        isLoading={false}
        onLoadMore={list.loadMore}
        renderEmptyState={renderNoResults}
        UNSAFE_style={{ maxHeight: positionProps.style?.maxHeight }}
        UNSAFE_className={cn('outline-none')}
      />
    </FocusScope>
  );

  const popover = (
    <Popover
      isOpen={state.isOpen}
      onClose={state.close}
      popOverStyles={popOverStyles}
      ref={popoverRef}
      style={getPopoverPositionStyles(
        positionProps,
        unwrappedTriggerRef,
        inputRef
      )}
      placement={placement}
    >
      {listBox}
    </Popover>
  );

  const renderLoader = (): React.ReactElement | undefined => {
    return (
      (list.loadingState === 'filtering' ||
        list.loadingState === 'loadingMore') && (
        <Oval3LoaderIcon
          width={18}
          height={14}
          color={'#2563EB'}
          strokeWidth={5}
          secondaryColor={'transparent'}
        />
      )
    );
  };

  const handleKeyDown = (event) => {
    const { collection, setOpen, setSelectedKey } = state;
    if (event.key === 'Enter' && collection.size > 0) {
      event.preventDefault();
      setSelectedKey(collection.getFirstKey());
      setOpen(false);
    }
  };

  return (
    <>
      <MediaQuery minWidth={769}>
        <div className={cn('relative', fieldContainerStyles)}>
          <Label
            {...labelProps}
            labelClassName={cn('mb-1', 'informedInputLabelText')}
          >
            {label}
          </Label>
          <FocusRing
            autoFocus={autoFocus}
            within
            focusClassProps={`ring-offset-0 ${
              error
                ? 'ring-0 border-1 border-red-400'
                : isFocus
                  ? `ring ring-blue-200`
                  : ''
            }`}
          >
            <div
              className={cn(
                getComboBoxWrapperWithPopOverStyles(
                  isFocus,
                  error,
                  isDisabled,
                  wrapperWithPopOverStyles
                ),
                'informedInputContainerClassName'
              )}
              {...getDataMaskAttribute(enableDataMask)}
            >
              {/* @ts-ignore */}
              <input
                {...inputProps}
                ref={inputRef as RefObject<HTMLInputElement>}
                onFocus={onFocus}
                onBlur={onBlur}
                className={cn(
                  isFocus
                    ? 'outline-none h-20px'
                    : error
                      ? 'placeholder-red-800 outline-none h-20px'
                      : 'outline-none h-20px',
                  inputFieldStyles,
                  'informedInputField'
                )}
                onKeyDown={handleKeyDown}
              />
              <LoaderWrapper>{renderLoader()}</LoaderWrapper>
              <HiddenSelect
                state={state}
                triggerRef={unwrappedTriggerRef}
                label={label}
              />
              <PressResponder {...mergeProps(hoverProps, triggerProps)}>
                <ComboBoxButton
                  buttonStyles={cn(buttonStyles, 'py-6px')}
                  state={state}
                  triggerProps={triggerProps}
                  ref={triggerRef as unknown as RefObject<HTMLElement>}
                  hoverProps={hoverProps}
                  focusProps={focusProps}
                />
              </PressResponder>
            </div>
          </FocusRing>
          <TextFieldHint
            id={'combobox-hint'}
            errorText={isFocus ? '' : error}
            hint={helperText}
            hintClassName={hintClassName}
          />
          {state.isOpen && popover}
        </div>
      </MediaQuery>
      <MediaQuery maxWidth={768}>
        <MobileComboBox
          {...props}
          getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
          items={list.items}
          selectedKey={state.selectedKey}
          renderLoader={renderLoader}
          hintClassName={hintClassName}
          handleKeyDown={handleKeyDown}
          enableDataMask={enableDataMask}
        />
      </MediaQuery>
    </>
  );
}
